// Page PUBLIQUE d'une invitation à une séance.
//
// Rendue AVANT la vérification de session (voir `App.tsx`) : la personne
// invitée n'a pas de compte et n'en aura pas. Elle lit qui l'invite, à quoi et
// quand, puis accepte ou décline. Si elle accepte, le lien de la
// visioconférence apparaît ici — et cette même adresse le lui redonnera
// jusqu'au lendemain de la séance.
import { useEffect, useState } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, buttonClass, ErrorBanner, Eyebrow, Spinner } from '../components/ui';
import { LogoMark, Wordmark } from '../components/Logo';
import { LegalFooter } from '../legal/LegalPages';
import { googleCalendarLink, icsHref } from '../calendar/api';
import { usePublicSessionInvitation, useRespondInvitation, type FormatKind, type PublicSessionInvitation } from './api';
import '../auth/auth.css';
import './invitations.css';

/** L'heure est écrite dans le fuseau de l'appareil qui lit, et le dit. */
function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });
}

function RdvFacts({ inv }: { inv: PublicSessionInvitation }) {
  return (
    <dl className="rdv-facts">
      <div>
        <dt>Quand</dt>
        <dd>
          {inv.startsAt
            ? formatWhen(inv.startsAt)
            : inv.choosing && inv.status === 'pending'
              ? 'À choisir ci-dessous'
              : 'Aucun créneau retenu'}
        </dd>
      </div>
      <div>
        <dt>Durée</dt>
        <dd>{inv.durationMin} minutes</dd>
      </div>
      <div>
        <dt>Où</dt>
        <dd>
          {inv.formatOptions.length > 1
            ? `Au choix : ${inv.formatOptions.map((f) => f.label).join(', ')}`
            : inv.mode === 'onsite' ? `Sur place : ${inv.location ?? ''}` : `En visioconférence, sur ${inv.providerLabel}`}
        </dd>
      </div>
    </dl>
  );
}

function Accepted({ inv }: { inv: PublicSessionInvitation }) {
  const [copied, setCopied] = useState(false);
  const who = inv.organizerName || 'la personne qui vous a invité·e';
  if (inv.mode === 'onsite') {
    return (
      <p className="rdv-state" role="status">
        <strong>Rendez-vous confirmé.</strong> Rendez-vous sur place : {inv.location}. Pour tout empêchement,
        écrivez à {who}.
      </p>
    );
  }
  if (!inv.meetingUrl) {
    return (
      <p className="rdv-state" role="status">
        <strong>Rendez-vous confirmé.</strong> Le lien de la visioconférence vous sera transmis par {who} ; il
        apparaîtra aussi sur cette page.
      </p>
    );
  }
  return (
    <div className="rdv-state" role="status">
      <p>
        <strong>Rendez-vous confirmé.</strong> Le moment venu, rejoignez la visioconférence par ce lien :
      </p>
      <a className={`${buttonClass('accent')} rdv-join`} href={inv.meetingUrl} target="_blank" rel="noreferrer noopener">
        Rejoindre sur {inv.providerLabel}
      </a>
      <input className="zy-input inv-link" readOnly value={inv.meetingUrl} aria-label="Lien de la visioconférence" onFocus={(e) => e.target.select()} />
      <Button
        type="button"
        variant="ghost"
        onClick={() => {
          navigator.clipboard.writeText(inv.meetingUrl ?? '').then(() => setCopied(true), () => setCopied(false));
        }}
      >
        {copied ? 'Lien copié' : 'Copier le lien'}
      </Button>
      <p className="auth-notice">Gardez cette page : elle vous redonnera le lien jusqu’au lendemain du rendez-vous.</p>
    </div>
  );
}

/**
 * Après réservation (057) : changer de créneau ou de format parmi ceux encore
 * libres, ou annuler. La personne qui invite est prévenue par e-mail.
 */
function ChangeBooking({ token, inv }: { token: string; inv: PublicSessionInvitation }) {
  const respond = useRespondInvitation(token);
  const [mode, setMode] = useState<'idle' | 'reschedule' | 'cancel'>('idle');
  const [slot, setSlot] = useState<string | null>(null);
  const [format, setFormat] = useState<FormatKind | null>(null);
  const [reason, setReason] = useState('');
  const who = inv.organizerName || 'la personne qui vous a invité·e';
  if (!inv.canCancel) return null;
  const error = respond.error ? (
    <ErrorBanner>
      {respond.error instanceof ApiRequestError ? respond.error.message : 'Votre demande n’a pas pu être enregistrée.'}
    </ErrorBanner>
  ) : null;

  if (mode === 'reschedule') {
    return (
      <div className="rdv-change">
        {inv.changeSlots.length > 0 ? (
          <fieldset className="inv-choice inv-slot-choices">
            <legend className="zy-field__label">Choisissez un autre créneau</legend>
            {inv.changeSlots.map((s) => (
              <label key={s} className={`inv-slot-choice${slot === s ? ' inv-slot-choice-on' : ''}`}>
                <input className="zy-radio" type="radio" name="rdv-new-slot" checked={slot === s} onChange={() => setSlot(s)} />
                {formatWhen(s)}
              </label>
            ))}
          </fieldset>
        ) : null}
        {inv.changeFormats.length > 1 ? (
          <fieldset className="inv-choice inv-slot-choices">
            <legend className="zy-field__label">Ou changez de format</legend>
            {inv.changeFormats.map((f) => (
              <label key={f.value} className={`inv-slot-choice${format === f.value ? ' inv-slot-choice-on' : ''}`}>
                <input className="zy-radio" type="radio" name="rdv-new-format" checked={format === f.value} onChange={() => setFormat(f.value)} />
                {f.label}
              </label>
            ))}
          </fieldset>
        ) : null}
        <div className="rdv-buttons">
          <Button
            variant="accent"
            loading={respond.isPending}
            disabled={!slot && !format}
            onClick={() => respond.mutate(
              { action: 'reschedule', ...(slot ? { startsAt: slot } : {}), ...(format ? { format } : {}) },
              { onSuccess: () => setMode('idle') },
            )}
          >
            Confirmer le changement
          </Button>
          <Button variant="ghost" onClick={() => setMode('idle')}>Retour</Button>
        </div>
        {error}
      </div>
    );
  }
  if (mode === 'cancel') {
    return (
      <form
        className="auth-form rdv-change"
        onSubmit={(e) => {
          e.preventDefault();
          respond.mutate({ action: 'cancel', reason });
        }}
      >
        <label className="zy-field">
          <span className="zy-field__label">Un mot pour {who} (facultatif)</span>
          <textarea className="zy-input" rows={3} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
        </label>
        <div className="rdv-buttons">
          <Button type="submit" variant="danger" loading={respond.isPending}>Annuler le rendez-vous</Button>
          <Button type="button" variant="ghost" onClick={() => setMode('idle')}>Retour</Button>
        </div>
        {error}
      </form>
    );
  }
  return (
    <div className="rdv-buttons rdv-change">
      {inv.canReschedule ? <Button variant="ghost" onClick={() => setMode('reschedule')}>Modifier mon rendez-vous</Button> : null}
      <Button variant="ghost" onClick={() => setMode('cancel')}>Je ne peux plus venir</Button>
    </div>
  );
}

/** Le rendez-vous dans SON agenda : Google, ou un fichier pour Outlook et Apple. */
function AddToMyCalendar({ inv }: { inv: PublicSessionInvitation }) {
  if (!inv.startsAt) return null;
  const event = {
    title: inv.subject,
    startsAt: inv.startsAt,
    durationMin: inv.durationMin,
    details: inv.mode === 'onsite' ? (inv.location ?? '') : (inv.meetingUrl ?? ''),
  };
  return (
    <div className="rdv-buttons rdv-change">
      <a className={buttonClass('ghost')} href={googleCalendarLink(event)} target="_blank" rel="noreferrer noopener">
        📅 Ajouter à Google Agenda
      </a>
      <a className={buttonClass('ghost')} href={icsHref(event)} download="rendez-vous.ics">
        Outlook, Apple (.ics)
      </a>
    </div>
  );
}

function Answer({ token, inv }: { token: string; inv: PublicSessionInvitation }) {
  const respond = useRespondInvitation(token);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  // Au choix : le créneau coché. Un créneau pris entre-temps disparaît au
  // rechargement de l'invitation (le serveur répond « plus disponible »).
  const [slot, setSlot] = useState<string | null>(null);
  // Formats au choix (055) : sur place, Google Meet, Microsoft Teams.
  const [format, setFormat] = useState<FormatKind | null>(null);
  const choosingFormat = inv.formatOptions.length > 1;
  const who = inv.organizerName || 'la personne qui vous a invité·e';

  if (inv.ended) {
    return (
      <p className="rdv-state">
        {inv.choosing ? 'Plus aucun des créneaux proposés n’est disponible.' : 'Ce créneau est passé.'} Si vous
        souhaitez toujours échanger, écrivez à {who}.
      </p>
    );
  }
  const needSlot = (inv.choosing && !slot) || (choosingFormat && !format);
  return (
    <>
      {inv.choosing && !declining ? (
        <fieldset className="inv-choice inv-slot-choices">
          <legend className="zy-field__label">Choisissez le créneau qui vous convient</legend>
          {inv.slots.map((s) => (
            <label key={s} className={`inv-slot-choice${slot === s ? ' inv-slot-choice-on' : ''}`}>
              <input className="zy-radio" type="radio" name="rdv-slot" checked={slot === s} onChange={() => setSlot(s)} />
              {formatWhen(s)}
            </label>
          ))}
        </fieldset>
      ) : null}
      {choosingFormat && !declining ? (
        <fieldset className="inv-choice inv-slot-choices">
          <legend className="zy-field__label">Comment souhaitez-vous échanger ?</legend>
          {inv.formatOptions.map((f) => (
            <label key={f.value} className={`inv-slot-choice${format === f.value ? ' inv-slot-choice-on' : ''}`}>
              <input className="zy-radio" type="radio" name="rdv-format" checked={format === f.value} onChange={() => setFormat(f.value)} />
              {f.value === 'onsite' && inv.location ? `${f.label} : ${inv.location}` : f.label}
            </label>
          ))}
        </fieldset>
      ) : null}
      {declining ? (
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            respond.mutate({ accept: false, reason });
          }}
        >
          <label className="zy-field">
            <span className="zy-field__label">Un mot pour {who} (facultatif)</span>
            <textarea
              className="zy-input"
              rows={3}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Pas disponible ce jour-là, mais la semaine suivante oui."
            />
          </label>
          <div className="rdv-buttons">
            <Button type="submit" variant="ghost" loading={respond.isPending}>Décliner l’invitation</Button>
            <Button type="button" variant="ghost" onClick={() => setDeclining(false)}>Retour</Button>
          </div>
        </form>
      ) : (
        <div className="rdv-buttons">
          <Button
            variant="accent"
            loading={respond.isPending}
            disabled={needSlot}
            onClick={() => respond.mutate({ accept: true, ...(slot ? { startsAt: slot } : {}), ...(format ? { format } : {}) })}
          >
            {inv.choosing || choosingFormat ? 'Je réserve' : 'J’accepte'}
          </Button>
          <Button variant="ghost" disabled={respond.isPending} onClick={() => setDeclining(true)}>
            Je ne peux pas
          </Button>
        </div>
      )}
      {respond.error ? (
        <ErrorBanner>
          {respond.error instanceof ApiRequestError ? respond.error.message : 'Votre réponse n’a pas pu être enregistrée.'}
        </ErrorBanner>
      ) : null}
    </>
  );
}

export function PublicInvitationPage({ token }: { token: string }) {
  const query = usePublicSessionInvitation(token);

  // Le jeton est dans l'URL : il ne doit pas partir en Referer vers un tiers
  // (la page mène à Google Meet ou à Teams).
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  if (query.isPending) {
    return (
      <div className="app-loading">
        <Spinner label="Ouverture de l’invitation…" />
      </div>
    );
  }

  const inv = query.data;
  return (
    <main className="auth">
      <div className="auth-card rdv-card">
        <div className="auth-brand">
          <LogoMark size={32} />
          <Wordmark className="auth-brand-name" />
        </div>

        {!inv ? (
          <>
            <h1 className="t-h2">Invitation plus valide</h1>
            <p className="auth-lede">
              {query.error instanceof ApiRequestError && query.error.status === 404
                ? 'Elle a peut-être été annulée, remplacée, ou le rendez-vous est passé. Demandez un nouveau lien à la personne qui vous l’a envoyé.'
                : 'Le serveur est momentanément injoignable. Réessayez dans un instant.'}
            </p>
          </>
        ) : (
          <>
            <Eyebrow>Invitation{inv.organizationName ? ` · ${inv.organizationName}` : ''}</Eyebrow>
            <h1 className="t-h2">{inv.subject}</h1>
            <p className="auth-lede">
              Bonjour {inv.inviteeName}, {inv.organizerName || 'une équipe de recherche'} vous propose{' '}
              {inv.mode === 'onsite' ? 'un rendez-vous sur place' : 'un échange en visioconférence'}.
            </p>
            <RdvFacts inv={inv} />
            {inv.message ? <p className="rdv-message">{inv.message}</p> : null}

            {inv.cancelled ? (
              <p className="rdv-state" role="status">
                Ce rendez-vous a été annulé. {inv.organizerName || 'La personne qui vous a invité·e'} reviendra
                vers vous si besoin.
              </p>
            ) : inv.status === 'accepted' ? (
              <>
                <Accepted inv={inv} />
                <AddToMyCalendar inv={inv} />
                <ChangeBooking token={token} inv={inv} />
              </>
            ) : inv.status === 'declined' ? (
              <p className="rdv-state" role="status">
                {inv.rescheduledAt ? 'Vous avez annulé ce rendez-vous.' : 'Vous avez décliné cette invitation.'}{' '}
                {inv.organizerName || 'La personne qui vous a invité·e'} en est
                informé·e.
              </p>
            ) : (
              <Answer token={token} inv={inv} />
            )}
            <p className="auth-notice">
              Votre nom et votre adresse ne servent qu’à organiser ce rendez-vous. Pour les faire supprimer, écrivez à
              la personne qui vous a invité·e.
            </p>
          </>
        )}
        <LegalFooter compact />
      </div>
    </main>
  );
}
